import { useEffect, useState } from 'react';
import { navItems } from '@/mocks/content';

export default function SiteNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeId, setActiveId] = useState('overview');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const sections = navItems
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => Boolean(el));
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 },
    );
    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleNav = (id: string) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled || menuOpen
          ? 'bg-background-50/95 backdrop-blur border-b border-background-200'
          : 'bg-transparent'
      }`}
    >
      <nav className="w-full px-4 md:px-8 h-16 flex items-center justify-between">
        <button
          type="button"
          onClick={() => handleNav('overview')}
          className={`flex items-center gap-2 font-heading text-lg md:text-xl font-bold tracking-wide cursor-pointer transition-colors ${
            scrolled || menuOpen ? 'text-foreground-900' : 'text-background-50'
          }`}
        >
          <span className={`w-8 h-8 flex items-center justify-center rounded-md text-base ${
            scrolled || menuOpen ? 'bg-primary-500 text-background-50' : 'bg-background-50/20 text-background-50'
          }`}>
            <i className="ri-book-marked-line" />
          </span>
          漂亮的保姆
        </button>

        <ul className={`hidden lg:flex items-center gap-1 ${
          scrolled ? 'text-foreground-700' : 'text-background-50/90'
        }`}>
          {navItems.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => handleNav(item.id)}
                className={`px-3 py-2 text-sm rounded-md whitespace-nowrap cursor-pointer transition-colors hover:text-primary-400 ${
                  activeId === item.id ? 'text-primary-400 font-medium' : ''
                }`}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>

        <button
          type="button"
          aria-label="打开导航菜单"
          onClick={() => setMenuOpen((v) => !v)}
          className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md text-2xl cursor-pointer ${
            scrolled || menuOpen ? 'text-foreground-900' : 'text-background-50'
          }`}
        >
          <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'} />
        </button>
      </nav>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-3">
          <ul className="grid grid-cols-2 gap-1">
            {navItems.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => handleNav(item.id)}
                  className={`w-full flex items-center gap-2 px-3 py-3 rounded-md text-sm cursor-pointer ${
                    activeId === item.id
                      ? 'bg-primary-100 text-primary-800'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  <i className={item.icon} />
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  );
}